import { useEffect, useState } from "react";
import client from "@/api";

/**
 * Shows a marketplace listing's primary cover image directly on the card.
 * Uses the existing `/listings/:id/media/:mediaId/download` endpoint (auth-aware
 * via axios `client`, no img-src workaround needed). Falls back to the provided
 * `placeholder` node when the listing has no image or the fetch fails.
 *
 * Business logic, upload rules, storage, and endpoints are NOT changed by
 * this component — it only reads the existing primary media on the card.
 */
export default function ListingCoverImage({ listingId, coverImageId, alt, placeholder }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!coverImageId) { setUrl(null); return; }
    let objUrl = null;
    let cancelled = false;
    client
      .get(`/listings/${listingId}/media/${coverImageId}/download`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(res.data);
        setUrl(objUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [listingId, coverImageId]);

  if (!coverImageId || failed) return placeholder ?? null;
  if (!url) return <div className="listing-cover-shimmer" aria-hidden="true" />;
  return (
    <img
      src={url}
      alt={alt || ""}
      className="listing-cover-img"
      loading="lazy"
      data-testid="listing-cover-image"
    />
  );
}
